/**
 * Where a portaled popup (a Select menu, a context menu, a help message) should mount (OWA-955).
 *
 * The page-level portal nodes (#portal-root, and #portal-select inside it) are children of <body>.
 * While a MUI modal (Dialog, temporary Drawer, Popover, Menu) is open, MUI's ModalManager sets
 * aria-hidden="true" on every body child except the modal, so a popup mounted there from inside the
 * modal is visible but hidden from screen readers. A Dialog also marks its Paper role="dialog" and
 * aria-modal="true", and some screen readers treat anything outside that element as inert, so inside
 * a Dialog the popup mounts in the dialog element itself.
 *
 * Popups mounted here must be position: fixed. The dialog element is a scroll container, which clips
 * absolute-positioned children but not fixed ones. A transformed dialog element (a draggable Paper)
 * would also offset fixed children; the one draggable dialog in the app renders its Selects inline.
 *
 * @param anchor Element the popup opens from (the Select control, the right-clicked wrapper).
 * @param fallback The page-level portal node the popup uses outside a modal. A fallback the caller
 *                 chose inside the chosen modal surface is kept.
 */
export function popupContainerFor<T extends HTMLElement | null>(anchor: Element | null | undefined, fallback: T): HTMLElement | T {
    const modalRoot = anchor?.closest<HTMLElement>('.MuiModal-root');
    if (!anchor || !modalRoot) return fallback;

    const dialog = anchor.closest<HTMLElement>('[role="dialog"]');
    const surface = dialog && modalRoot.contains(dialog) ? dialog : modalRoot;
    return fallback && surface.contains(fallback) ? fallback : surface;
}
